import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { ahuBoardApi, usePatchAhuTunnelMutation } from 'app/services/ahuBoardApi';
import {
  useGetAhuCasingQuery,
  useGetAhuFloorQuery,
  usePatchAhuCasingMutation,
  usePatchAhuFloorMutation,
} from 'app/services/ahuBoardFormsApi';
import type {
  IAhuBoardSection,
  IAhuBoardTunnel,
  IAhuBoardUnit,
  ICmsAhuDoc,
} from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  refetchHybridLugPlacements,
  refetchLugPlacementsAfterGeometryChange,
} from '../../boardActions';
import { validateTunnel } from '../../validation/validationController';
import { formatTunnelAddress } from '../../addressing';
import PaginatedSelect from '../../forms/PaginatedSelect';
import { formatLegacyNumber } from '../../forms/formUtils';
import { cmsWidthValues, cmsHeightValues } from '../../forms/cmsDoc';
import TunnelFaceFlagMultiSelect from './TunnelFaceFlagMultiSelect';
import {
  baseSidesPatchBody,
  createBaseSidesWriter,
  dropNoopBaseSides,
  dropSettledBaseSides,
} from './baseSidesWriter';

/**
 * Phase 6e1 — tunnel cabinet rows (§7.3.1, tab.ahu.tunnel.cabinet.js +
 * tab.ahu.cabinet.tmpl, markup faithful — debug class `2222` dropped per
 * [NEG]#21). W/H selects + readonly L + 4 paginated selects (casing outer
 * skin / skin finish → PATCH casing; floor under-insulation / under-liner →
 * PATCH floor).
 *
 * §19.2 tunnel-save cascade reproduced on PATCH success:
 *   width/height → ahu.validateTunnel + `change:tunnel:dimensions
 *   {tunnel_id, render:true, accessories_fetch:true}` + `changed:tunnel_<id>:sizes`
 *   (+ `changed:tunnel_<id>:width` width-only, + `get:overall:height(true)`
 *   for height) + a lug placement re-read in place of legacy's
 *   `tunnelLugs:recalcPlacement` queue (§7.3.1 model listener) — the server
 *   re-lays the runs in the PATCH itself, AHU-wide on a hybrid unit and for
 *   this tunnel on a standard one (refetchLugPlacementsAfterGeometryChange).
 * Pre-save the cabinet fires `change:tunnel:widthOrHeight` (+ `:<id>`).
 */

interface TunnelCabinetRowProps {
  ahu: IAhuBoardUnit;
  tunnel: IAhuBoardTunnel;
  /** Every tunnel on this AHU — needed to find back-to-back/OU peers for the
   * Y-Offset visibility rule and to resolve the (row, level) anchor tunnel. */
  tunnels: IAhuBoardTunnel[];
  /** Every section on this AHU — the dual-tunnel pairing (`sitting_on_id`)
   * crosses tunnels, so the X-Offset gate below cannot be answered from this
   * tunnel's own rows. */
  sections: IAhuBoardSection[];
  cms: ICmsAhuDoc | undefined;
  mfgId: number;
  /** Hybrid AHU write gate — Walls/Roof/Floor is Power-user only. */
  isPowerUser: boolean;
}

/* Short labels, not legacy's "Ups End Wall"/"Ds End Wall"/"Left Side Wall"/…
 * (tab.ahu.cabinet.tmpl): the checkbox popup and the one-line summary share
 * this list, and the long forms overflowed the 185px summary as soon as three
 * faces were on. Deliberate deviation from KA-x-user-skill. */
const FACE_FLAG_FIELDS = [
  { name: 'has_upstream_end_wall', label: 'Upstream' },
  { name: 'has_downstream_end_wall', label: 'Downstream' },
  { name: 'has_left_side_wall', label: 'Left' },
  { name: 'has_right_side_wall', label: 'Right' },
  { name: 'has_roof', label: 'Roof' },
  { name: 'has_floor', label: 'Floor' },
] as const;
type FaceFlagField = (typeof FACE_FLAG_FIELDS)[number]['name'];

/* "Bases" — the same widget, one row below, hybrid AHUs only. Four sides of
 * the tunnel's base perimeter frame, with the SAME four labels and the SAME
 * axis conventions as the first four rows above: `upstream`/`downstream` are
 * the physical X− / X+ ends whichever way the airflow runs, `left`/`right` are
 * airflow-relative. That is the point of matching them — a user who unticks
 * the Left wall and the Left base means one side of one tunnel.
 * `django/ahu/base_sides.py` is where the convention is written down.
 *
 * `name` is the OVERRIDE column each checkbox writes; what it SHOWS is
 * `tunnel.base_sides[side]`, the state the server actually built. A tunnel
 * with no override has no value of its own in these columns at all. */
const BASE_SIDE_FIELDS = [
  { name: 'base_side_upstream', side: 'upstream', label: 'Upstream' },
  { name: 'base_side_downstream', side: 'downstream', label: 'Downstream' },
  { name: 'base_side_left', side: 'left', label: 'Left' },
  { name: 'base_side_right', side: 'right', label: 'Right' },
] as const;
type BaseSideKey = (typeof BASE_SIDE_FIELDS)[number]['side'];

/* The "Default" row of the same multi-select. Not a face and not a column:
 * it is a VIEW of the four override columns being NULL, which is already how
 * the data model says "let the automatic rules decide". Ticking it clears all
 * four; a manual tick that lands anywhere else writes a column and so unticks
 * it again, for free. A tick that lands back ON the automatic answer for all
 * four sides is Default by another name, and the server saves NULLs for it
 * (`tunnel_viewset._collapse_base_sides_to_default`) -- so the box ticks
 * itself again when that response merges into the row.
 *
 * Deliberately not a stored flag and not a materialised set of values: NULL is
 * re-evaluated live against the layout on every read, so a Default tunnel
 * already follows geometry changes with no write and no extra request. Storing
 * the computed values would turn that into one row-write per tunnel per
 * geometry edit, each taking the unit's advisory lock. */
const BASE_DEFAULT_FIELD = 'base_sides_default';

/* How long the Bases row waits after a tick before it writes.
 *
 * Long enough that ticking three boxes in a row is one PATCH -- each one
 * costs a whole-unit base-group recompute server-side -- and short enough
 * that a single tick is saved while the user is still looking at the popup.
 * Closing the popup flushes early, so this is never a wait a deliberate
 * close has to sit through. */
const BASE_SIDES_DEBOUNCE_MS = 400;
/* Default FIRST, with a hairline under it (Shushanik, 2026-10-30): it is not a
 * fifth face but the switch that governs the four below it, so it reads as a
 * heading rather than as another side. The order is the list's order
 * throughout -- the checkboxes, the one-line summary of what is ticked, and
 * its tooltip -- but the summary leaves Default out entirely (it is not a
 * side, and reading it beside four of them suggested it was). Nothing else
 * about the row changes: the keys, the writes and what each box does are
 * keyed by `name`, not by position. */
const BASE_SELECT_OPTIONS = [
  { name: BASE_DEFAULT_FIELD, label: 'Default', dividerAfter: true, hideFromSummary: true },
  ...BASE_SIDE_FIELDS.map(({ name, label }) => ({ name, label })),
];

const TUNNEL_OFFSET_DIRECTIONS: Array<{
  value: NonNullable<IAhuBoardTunnel['tunnel_offset_direction']>;
  label: string;
}> = [
  { value: 'left_aligned', label: 'Left Aligned' },
  { value: 'centered', label: 'Centered' },
  { value: 'right_aligned', label: 'Right Aligned' },
  { value: 'user_defined_left', label: 'User Defined Left' },
  { value: 'user_defined_right', label: 'User Defined Right' },
];

/**
 * Y-Offset visibility (§7.3.1 tab.ahu.tunnel.cabinet.js):
 *  - Hybrid: shown on every tunnel except the 1:1:1 anchor.
 *  - Non-hybrid: shown only for a back-to-back peer (position > 1 vs. the
 *    row/level's position-1 tunnel) or an OU upper tunnel (level > 1 vs.
 *    level-1, position 1) whose width differs from its peer's by > 1e-6.
 */
const showYOffset = (
  tunnel: IAhuBoardTunnel,
  tunnels: IAhuBoardTunnel[],
  isHybrid: boolean | undefined
): boolean => {
  const row = tunnel.row ?? 1;
  const level = tunnel.level ?? 1;
  const position = tunnel.position ?? 1;

  if (isHybrid) {
    return !(row === 1 && level === 1 && position === 1);
  }

  const findPeer = () => {
    if (position > 1) {
      return tunnels.find(
        (t) => (t.row ?? 1) === row && (t.level ?? 1) === level && (t.position ?? 1) === position - 1
      );
    }
    if (level > 1) {
      return tunnels.find(
        (t) => (t.row ?? 1) === row && (t.level ?? 1) === level - 1 && (t.position ?? 1) === 1
      );
    }
    return undefined;
  };
  const peer = findPeer();
  if (!peer) return false;
  const widthDiff = Math.abs(Number(tunnel.width ?? 0) - Number(peer.width ?? 0));
  return widthDiff > 1e-6;
};

/**
 * The tunnel whose X-Offset drives this one, or null when this tunnel owns its
 * own — i.e. for every tunnel that is not the SECOND half of a dual-tunnel
 * component (Heat Wheel / Plate Heatex).
 *
 * A dual-tunnel component is one physical box spanning two tunnels: the
 * primary section, and the secondary one carrying `sitting_on_id`
 * (ComponentFormHost's bound-section rule). Its two halves have to cover the
 * same span on X, and on a hybrid AHU `x_offset` is the tunnel's own UPSTREAM
 * edge — the LEFT edge going left-to-right, the RIGHT edge going
 * right-to-left — so the two tunnels of a counter-flowing pair need DIFFERENT
 * numbers for the same span (`hybridXOffset.offsetForPhysicalSpan`), and a
 * number that also absorbs whatever sits upstream of the component inside each
 * tunnel. Only the primary's is editable, and the server derives the other
 * from it on every save (`ahu/dual_tunnel_x.py`).
 */
const xOffsetOwnerTunnel = (
  tunnel: IAhuBoardTunnel,
  tunnels: IAhuBoardTunnel[],
  sections: IAhuBoardSection[]
): IAhuBoardTunnel | null => {
  const bound = sections.find(
    (s) => s.tunnel_id === tunnel.id && s.sitting_on_id != null
  );
  if (!bound) return null;
  const primary = sections.find((s) => s.id === bound.sitting_on_id);
  // A pair inside one tunnel has nothing to follow; nor has a half whose
  // partner the board has not loaded (or has just deleted).
  if (!primary || primary.tunnel_id === tunnel.id) return null;
  return tunnels.find((t) => t.id === primary.tunnel_id) ?? null;
};

/**
 * The mirror of `xOffsetOwnerTunnel`: every tunnel whose X-Offset FOLLOWS this one,
 * because it holds the second half of a dual-tunnel component hosted here. Empty for
 * every tunnel that drives nothing.
 *
 * Plural: one tunnel can host the primary half of more than one component (nothing
 * stops it — `section_viewset`'s create guard only rejects a tunnel that already holds
 * a `sitting_on` section, i.e. a SECOND half), and a follower can in turn be the
 * primary of its own pair, which is what `dual_tunnel_x.MAX_PASSES` is for.
 */
const xOffsetFollowerTunnelIds = (
  tunnel: IAhuBoardTunnel,
  sections: IAhuBoardSection[]
): number[] => {
  const own = new Set(sections.filter((s) => s.tunnel_id === tunnel.id).map((s) => s.id));
  const direct = sections
    .filter((s) => s.sitting_on_id != null && own.has(s.sitting_on_id) && s.tunnel_id !== tunnel.id)
    .map((s) => s.tunnel_id);
  // Walk the chain, with the visited set as the guard against a `sitting_on` cycle.
  const found = new Set<number>(direct);
  const queue = [...direct];
  while (queue.length) {
    const next = queue.shift() as number;
    const ids = new Set(sections.filter((s) => s.tunnel_id === next).map((s) => s.id));
    sections
      .filter((s) => s.sitting_on_id != null && ids.has(s.sitting_on_id))
      .forEach((s) => {
        if (s.tunnel_id !== tunnel.id && !found.has(s.tunnel_id)) {
          found.add(s.tunnel_id);
          queue.push(s.tunnel_id);
        }
      });
  }
  return [...found];
};

const TunnelCabinetRow = ({
  ahu,
  tunnel,
  tunnels,
  sections,
  cms,
  mfgId,
  isPowerUser,
}: TunnelCabinetRowProps) => {
  const dispatch = useAppDispatch();
  const [patchTunnel] = usePatchAhuTunnelMutation();
  const [patchCasing] = usePatchAhuCasingMutation();
  const [patchFloor] = usePatchAhuFloorMutation();
  const [emptyRows, setEmptyRows] = useState<Record<number, boolean>>({});
  const [tagValue, setTagValue] = useState(tunnel.custom_name ?? '');
  const [pendingFaceFlags, setPendingFaceFlags] = useState<Partial<Record<FaceFlagField, boolean>>>(
    {}
  );
  const [pendingBaseSides, setPendingBaseSides] = useState<Partial<Record<BaseSideKey, boolean>>>(
    {}
  );
  /** The same map, for the debounce timer and the in-flight settle to read:
   * both run outside a render and a captured state value would be stale. */
  const pendingBaseSidesRef = useRef<Partial<Record<BaseSideKey, boolean>>>({});

  // Outer-skin / skin-finish live on the CASING row, under-insulation /
  // under-liner on the FLOOR row (legacy tunnel.casing / tunnel.floor models
  // — the tunnel payload does not carry these fields).
  const { data: casing } = useGetAhuCasingQuery(
    tunnel.casing_id != null ? Number(tunnel.casing_id) : skipToken
  );
  const { data: floor } = useGetAhuFloorQuery(
    tunnel.floor_id != null ? Number(tunnel.floor_id) : skipToken
  );

  const widths = cmsWidthValues(cms);
  const heights = cmsHeightValues(cms);
  const ccid = ahu.construction_class_id ?? null;

  // W/H options are CMS value NAMES ("36.00" — value_names is "{:.2f}");
  // tunnel.width is a float (36) — match numerically so the native select's
  // selected option is correct (legacy: tunnel.width.toFixed(2) == name).
  const matchedDim = (values: Array<string | number>, raw: unknown): string => {
    const n = Number(raw);
    const hit = values.find((v) => Number(v) === n);
    return hit != null ? String(hit) : String(raw ?? '');
  };
  const widthValue = matchedDim(widths, tunnel.width);
  const heightValue = matchedDim(heights, tunnel.height);

  const changeTunnel = (name: 'width' | 'height', value: number) => {
    ahuBoardBus.emit('change:tunnel:widthOrHeight', { tunnel_id: tunnel.id, name, value });
    ahuBoardBus.emit(`change:tunnel:widthOrHeight:${tunnel.id}`);
    patchTunnel({ id: tunnel.id, ahuId: tunnel.ahu_id, data: { [name]: value } })
      .unwrap()
      .then(() => {
        // §19.2 tunnel cascade.
        void dispatch(validateTunnel(tunnel.id));
        ahuBoardBus.emit('change:tunnel:dimensions', {
          tunnel_id: tunnel.id,
          render: true,
          accessories_fetch: true,
        });
        ahuBoardBus.emit(`changed:tunnel_${tunnel.id}:sizes`);
        if (name === 'width') ahuBoardBus.emit(`changed:tunnel_${tunnel.id}:width`);
        if (name === 'height') ahuBoardBus.emit('get:overall:height', true);
        // The lugs are laid out server-side and this PATCH has already re-laid
        // them — AHU-wide on a hybrid unit, for this tunnel on a standard one
        // (width sets the rail the along-length lugs sit on and the span the
        // along-width ones are distributed over). So re-read the placements
        // rather than arm ReCalculate over a canvas the arming itself just
        // emptied. Measured in refetchHybridLugPlacements.
        void dispatch(
          refetchLugPlacementsAfterGeometryChange(tunnel.ahu_id, [tunnel.id], {
            hideWhilePending: true,
          })
        );
      })
      .catch(() => undefined);
  };

  const changeCasing = (field: 'outerliner_id' | 'finish_id', id: number | null) => {
    if (tunnel.casing_id == null) return;
    patchCasing({ id: Number(tunnel.casing_id), data: { [field]: id } })
      .unwrap()
      .then(() => {
        // QA5 #7 — write the new id into the tunnel's section rows so the
        // CABINET tab selects update without a refresh (legacy
        // tab.section.js:133-147 listens on tunnel.casing and re-getValues).
        dispatch(
          ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnel.id, (rows) =>
            rows.map((row) => ({ ...row, [field]: id }))
          )
        );
        if (field === 'outerliner_id') {
          ahuBoardBus.emit('change:tunnel:casing', { tunnel_id: tunnel.id });
          // [NEG]#58 — casing.callValidate passes the CASING id as tunnel id
          // (works because casing_id == tunnel_id in practice); we validate
          // the tunnel directly.
          void dispatch(validateTunnel(tunnel.id));
        }
      })
      .catch(() => undefined);
  };

  const changeFloor = (
    field: 'under_insulation_id' | 'underliner_id',
    id: number | null
  ) => {
    if (tunnel.floor_id == null) return;
    patchFloor({
      id: Number(tunnel.floor_id),
      data: { [field]: id, tunnel_id: tunnel.id },
    })
      .unwrap()
      .then(() => {
        ahuBoardBus.emit(`update:tunnel:${Number(tunnel.floor_id)}:fields`);
        // QA5 #7 — refetch the tunnel's section rows so the CABINET tab's
        // readonly Under-Insulation/Under-Liner rows refresh without a page
        // reload (legacy `update:tunnel:<floorId>:fields` listener,
        // tab.section.js:157-164).
        dispatch(
          ahuBoardApi.util.invalidateTags([{ type: 'AhuSections', id: tunnel.id }])
        );
        if (field === 'under_insulation_id') {
          // under-insulation thickness affects canvas Y (§3.1) — refetch
          // tunnels (the legacy cabinet does model.fetch()).
          dispatch(
            ahuBoardApi.util.invalidateTags([{ type: 'AhuTunnels', id: tunnel.ahu_id }])
          );
        }
      })
      .catch(() => undefined);
  };

  const saveTag = (value: string) => {
    if (value === (tunnel.custom_name ?? '')) return;
    patchTunnel({ id: tunnel.id, ahuId: tunnel.ahu_id, data: { custom_name: value } })
      .unwrap()
      .catch(() => undefined);
  };

  const toggleFaceFlag = (field: FaceFlagField) => {
    setPendingFaceFlags((prev) => {
      const current = field in prev ? prev[field] : Boolean(tunnel[field]);
      return { ...prev, [field]: !current };
    });
  };

  const faceFlagValue = (field: FaceFlagField): boolean =>
    field in pendingFaceFlags ? Boolean(pendingFaceFlags[field]) : Boolean(tunnel[field]);

  const faceFlagValues = Object.fromEntries(
    FACE_FLAG_FIELDS.map(({ name }) => [name, faceFlagValue(name)])
  );

  // `pendingFaceFlags` is cleared in the same `.finally()` as the popup
  // closes, so there is nothing left to re-send if the widget asks again.
  const saveFaceFlags = () => {
    if (Object.keys(pendingFaceFlags).length === 0) return undefined;
    const floorRemoved = pendingFaceFlags.has_floor === false;
    return patchTunnel({ id: tunnel.id, ahuId: tunnel.ahu_id, data: pendingFaceFlags })
      .unwrap()
      .then(() => {
        ahuBoardBus.emit('change:tunnel:dimensions', { tunnel_id: tunnel.id, render: true });
        // Unchecking Floor drops the whole floor build-up server-side —
        // AHUTunnelFloor.save() clears Under-Insulation and Under-Liner once
        // the last section's floor is gone (AHUTunnelViewSet.partial_update).
        // Those two rows read `getAhuFloor`, which this PATCH does not touch,
        // so without an explicit invalidate they keep showing the removed
        // materials until the board is reloaded — and the next edit to either
        // select would PATCH the stale sibling id straight back.
        if (floorRemoved) {
          if (tunnel.floor_id != null) {
            dispatch(
              ahuBoardApi.util.invalidateTags([
                { type: 'Ahu', id: `floor-${Number(tunnel.floor_id)}` },
              ])
            );
            ahuBoardBus.emit(`update:tunnel:${Number(tunnel.floor_id)}:fields`);
          }
          // CABINET tab mirrors both as readonly per-section rows.
          dispatch(ahuBoardApi.util.invalidateTags([{ type: 'AhuSections', id: tunnel.id }]));
        }
      })
      .catch(() => undefined)
      .finally(() => setPendingFaceFlags({}));
  };

  // ---------------------------------------------------------------------
  // "Bases" — the same widget on the four base-side overrides.
  // ---------------------------------------------------------------------

  /** What the checkbox shows: the state the server BUILT, with this session's
   * un-committed toggles on top. Not the override column — that is null on a
   * tunnel nobody has touched, which is the normal case and is not a state a
   * checkbox can render. `base_sides` is the server's answer after both the
   * automatic rule and any override, so an untouched tunnel's boxes come up
   * matching the base it actually has. */
  const baseSideValue = (side: BaseSideKey): boolean =>
    side in pendingBaseSides
      ? Boolean(pendingBaseSides[side])
      : Boolean(tunnel.base_sides?.[side] ?? true);

  /** Default is checked exactly when all four override columns are NULL --
   * never stored, always derived, so it cannot drift from the columns. A
   * pending manual toggle counts: the box unticks the moment the user picks a
   * side, before the save. */
  const onDefault =
    Object.keys(pendingBaseSides).length === 0 &&
    BASE_SIDE_FIELDS.every(({ name }) => tunnel[name] == null);

  const baseSideValues = {
    ...Object.fromEntries(
      BASE_SIDE_FIELDS.map(({ name, side }) => [name, baseSideValue(side)])
    ),
    [BASE_DEFAULT_FIELD]: onDefault,
  };

  /* A static version or an archived project is a record of a quote that was
   * sent; the server refuses the write (`ahu/static_protect.py`), so the boxes
   * are disabled rather than left to fail. */
  const basesLockedReason = (name: string): string | undefined => {
    if (ahu.static_version) {
      return 'This is a static version — its configuration cannot be changed.';
    }
    // The four sides only. Default itself stays clickable-looking but
    // `applyDefault` already refuses a second request while one is out.
    if (defaultSaving && name !== BASE_DEFAULT_FIELD) {
      return 'Resetting to the automatic bases…';
    }
    return undefined;
  };

  /** One Default reset at a time: set from the click, cleared when the write
   * settles. The reset is handed to the writer rather than sent here, so this
   * covers both halves of its life -- queued behind an in-flight side write,
   * and then out -- and a second click in either window asks for nothing. It
   * is also what locks the four side boxes (`basesLockedReason`). */
  const defaultSavingRef = useRef(false);
  /* State, not just the ref: the four side boxes have to RE-RENDER as
   * disabled while the Default save is out. With only a ref, a side ticked in
   * that window showed its new state, was dropped from `pendingBaseSides`
   * when the save landed, and was never sent -- the user saw a side they had
   * chosen that the base was not built or priced with. */
  const [defaultSaving, setDefaultSaving] = useState(false);

  const applyDefault = () => {
    // Already on Default: nothing to write. A PATCH here would still cost a
    // full base-group recompute server-side (1.9 s on a 17-tunnel unit), so
    // the no-op is worth the check.
    if (onDefault || defaultSavingRef.current) return;
    defaultSavingRef.current = true;
    setDefaultSaving(true);
    // Drop any un-committed side toggles, and the debounce window with them:
    // the user has just said "forget my selections", and leaving either in
    // place would write them back on top of the Default.
    baseSidesWriter.cancel();
    pendingBaseSidesRef.current = {};
    setPendingBaseSides({});
    // Then ask the WRITER for it rather than sending it here. A side PATCH
    // can still be in flight -- the reset is one click after a tick, and the
    // tick's save is 400 ms behind it -- and the two are not ordered by the
    // server: `partial_update` saves outside any transaction and takes the
    // unit lock later, in `recompute_base_groups`. The side write would then
    // land after the reset and put the pin back, with Default unticked,
    // which is the opposite of what the user last asked for. Queued behind
    // it, the four NULLs are the last thing written.
    defaultRequestedRef.current = true;
    baseSidesWriter.flush();
  };

  /* Everything the write needs, refreshed every render and read through the
   * ref: the writer is created ONCE -- its timer and its in-flight flag have
   * to survive re-renders -- so it must not close over this render's props. */
  const baseWriteContext = useRef({
    tunnelId: tunnel.id,
    ahuId: tunnel.ahu_id,
    serverSides: tunnel.base_sides,
  });
  baseWriteContext.current = {
    tunnelId: tunnel.id,
    ahuId: tunnel.ahu_id,
    serverSides: tunnel.base_sides,
  };

  /** A Default reset the writer has been asked for and not yet sent. Not
   * `defaultSaving`, which is the UI state: this one survives the wait behind
   * an in-flight side write. */
  const defaultRequestedRef = useRef(false);

  /** The column a Data Bar side writes, and the state the server currently
   * builds for it. Written once: `build`, the settle and the prune all have
   * to agree about both, and three copies of each is three chances to
   * disagree. Both read through `baseWriteContext`, so they are safe to hand
   * to the writer, which outlives this render. */
  const baseSideField = (side: BaseSideKey): string | undefined =>
    BASE_SIDE_FIELDS.find((f) => f.side === side)?.name;
  const serverBaseSide = (side: BaseSideKey): boolean =>
    Boolean(baseWriteContext.current.serverSides?.[side] ?? true);

  /** The four-NULL body, told apart from a side body by its values. */
  const isDefaultBody = (body: Record<string, boolean | null>): boolean =>
    Object.values(body).every((value) => value === null);

  /** Write the pending map: no-op entries pruned, ref first (the writer reads
   * it synchronously), then the state the checkboxes render from. */
  const writePendingBaseSides = (next: Partial<Record<BaseSideKey, boolean>>) => {
    const pruned = dropNoopBaseSides<BaseSideKey>(next, serverBaseSide);
    pendingBaseSidesRef.current = pruned;
    setPendingBaseSides(pruned);
  };

  /**
   * One debounced PATCH per burst of Bases toggles, one in flight at a time.
   *
   * `baseSidesWriter.ts` holds the rules and the tests; this is the wiring.
   * Four things it buys the row (Shushanik, 2026-10-30):
   *
   * · the save goes out on the toggle, not on the popup closing;
   * · four boxes ticked in one second are ONE request, not four;
   * · a toggle made while a PATCH is out waits for it instead of racing it,
   *   so two responses can never land on the tunnel row out of order (every
   *   response is merged into that row by `patchAhuTunnel`);
   * · the pending map is settled against the body that WAS sent, so each box
   *   falls back to the server's answer as its write lands, while a side the
   *   user moved meanwhile stays pending and goes out next.
   *
   * The boxes then show the response with no refresh and no reopening:
   * `baseSideValue` reads the pending map first and the row's `base_sides`
   * under it, and `onDefault` reads the row's four override columns — which
   * the response carries, NULLs included, so Default ticks itself the moment
   * the server decides four picks match the automatic answer.
   */
  const baseSidesWriter = useMemo(
    () =>
      createBaseSidesWriter<Record<string, boolean | null>>({
        delay: BASE_SIDES_DEBOUNCE_MS,
        build: () => {
          // Default first: it is the user's latest word on all four columns,
          // and `applyDefault` has already dropped every pending side.
          if (defaultRequestedRef.current) {
            return Object.fromEntries(BASE_SIDE_FIELDS.map(({ name }) => [name, null]));
          }
          return baseSidesPatchBody<BaseSideKey>(
            pendingBaseSidesRef.current,
            baseSideField,
            serverBaseSide
          );
        },
        send: (data) => {
          const { tunnelId, ahuId } = baseWriteContext.current;
          return patchTunnel({ id: tunnelId, ahuId, data })
            .unwrap()
            .then(() => {
              // Removing or restoring a base member re-merges the unit's base
              // groups, which is what the lug runs are laid out on — so the
              // same two follow-ups a dimension edit makes. The PATCH has
              // already re-laid them server-side (`recompute_base_groups` →
              // `sync_hybrid_lugs`), so re-READ the placements rather than
              // arming ReCalculate over a canvas the arming would empty.
              // AHU-wide: a shared group can span tunnels, so dropping this
              // tunnel's member moves the lugs on the neighbour that shared
              // it.
              ahuBoardBus.emit('change:tunnel:dimensions', {
                tunnel_id: tunnelId,
                render: true,
              });
              void dispatch(refetchHybridLugPlacements(ahuId, { hideWhilePending: true }));
              // The base is a section-level weight and cost
              // (`AHUSection.base_qty` counts the members that exist), and the
              // tunnel rows carry the weight totals the Data Bar prints.
              dispatch(
                ahuBoardApi.util.invalidateTags([
                  { type: 'AhuTunnels', id: ahuId },
                  { type: 'AhuSections', id: tunnelId },
                ])
              );
            });
        },
        onSettled: (data) => {
          if (isDefaultBody(data)) {
            defaultRequestedRef.current = false;
            defaultSavingRef.current = false;
            setDefaultSaving(false);
            return;
          }
          // The ref FIRST and synchronously, then the state from it. A
          // `setState` updater runs during the next render, so settling in
          // there left the ref holding the toggles this request had just
          // carried -- and the writer's queued follow-up, which reads the ref
          // the moment this returns, sent the identical body a second time
          // (seen live: closing the popup after a save made a duplicate
          // PATCH). The ref is the writer's source of truth; the state is
          // only what the checkboxes render from.
          const settled = dropSettledBaseSides<BaseSideKey>(
            pendingBaseSidesRef.current,
            data as Record<string, boolean>,
            baseSideField
          );
          writePendingBaseSides(settled);
        },
        // Nothing to send -- a box ticked off and back on, or a popup opened
        // and closed. Those toggles have to leave the map even though no
        // request carried them: see `dropNoopBaseSides`.
        onIdle: () => writePendingBaseSides(pendingBaseSidesRef.current),
      }),
    // Created once per mounted row: `patchTunnel` and `dispatch` are stable
    // RTK handles, and everything else is read through a ref.
    [dispatch, patchTunnel]
  );

  /* The row can go away with a toggle still inside its debounce window: the
   * user leaves the board, or the tunnel is deleted. (Not picking another
   * tunnel or collapsing the Data Bar — every tunnel's row stays mounted,
   * keyed by id, and the collapse is CSS.) Send it rather than drop it; a
   * flush with an empty window is a no-op, so the ordinary unmount costs
   * nothing. */
  useEffect(() => () => baseSidesWriter.flush(), [baseSidesWriter]);

  const toggleBaseSide = (name: string) => {
    if (name === BASE_DEFAULT_FIELD) {
      applyDefault();
      return;
    }
    const entry = BASE_SIDE_FIELDS.find((f) => f.name === name);
    if (!entry) return;
    // A manual pick unticks Default on its own: `onDefault` reads the pending
    // map, so the box clears on this keystroke rather than after the save.
    // Off the ref, not off the state: the state may not have caught up with
    // a settle that has just run, and two ticks in one tick of the event loop
    // would both read the same stale `prev`.
    pendingBaseSidesRef.current = {
      ...pendingBaseSidesRef.current,
      [entry.side]: !baseSideValue(entry.side),
    };
    setPendingBaseSides(pendingBaseSidesRef.current);
    // ...and the write follows a debounce window later, rather than waiting
    // for the popup to close.
    baseSidesWriter.schedule();
  };

  /* Closing the popup no longer sends the write — the writer has sent it, or
   * is about to — so this only brings the window forward. A popup opened and
   * closed with nothing ticked, or a box ticked and un-ticked back to where
   * the server is, still sends nothing: `build()` returns null for both.
   * Returns nothing, so the popup closes on the click instead of waiting for
   * a round trip. */
  const saveBaseSides = () => {
    baseSidesWriter.flush();
    return undefined;
  };


  // §19.2: offset/dimension edits hide this tunnel's lugs from the canvas
  // immediately, before the redraw the PATCH success triggers, so the redraw
  // never paints them at their now-stale position (legacy model/tunnel.js
  // onChange, run BEFORE model.set()).
  const changeOffsetField = (
    name: 'x_offset' | 'tunnel_offset_direction' | 'tunnel_offset',
    value: string | number
  ) => {
    if (!ahu.is_hybrid) {
      ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
        tunnel_id: tunnel.id,
        hide_from_canvas: true,
      });
    }
    patchTunnel({ id: tunnel.id, ahuId: tunnel.ahu_id, data: { [name]: value } })
      .unwrap()
      .then(() => {
        void dispatch(validateTunnel(tunnel.id));
        // Moving this tunnel along X moves the second half of every dual-tunnel
        // component it hosts — the server re-derives those tunnels' own `x_offset`
        // (`ahu/dual_tunnel_x.py`) and `refetchTunnelsAfterAddressingChange` brings the
        // new values back, which is enough for the canvas. Their ERRORS are not:
        // validation is per tunnel (`GET tunnels/<id>/validation`), so without this a
        // follower keeps the overlap/gap errors it had at its old position, and a stale
        // one disables Generate.
        if (name === 'x_offset') {
          xOffsetFollowerTunnelIds(tunnel, sections).forEach((id) => {
            void dispatch(validateTunnel(id));
          });
        }
        ahuBoardBus.emit('change:tunnel:dimensions', { tunnel_id: tunnel.id, render: true });
        // Same fork as changeTunnel: an offset moves the tunnel's span, so the
        // server re-lays the runs across it and `tunnel_positions` reattributes
        // the lugs between tunnels. Hiding and arming ReCalculate would strand
        // a placement the PATCH had already corrected.
        if (ahu.is_hybrid) {
          void dispatch(refetchHybridLugPlacements(tunnel.ahu_id, { hideWhilePending: true }));
        }
      })
      .catch(() => undefined);
  };

  const hiddenIfEmpty = (idx: number) =>
    emptyRows[idx] ? { display: 'none' } : undefined;
  const markEmpty = (idx: number) => (empty: boolean) =>
    setEmptyRows((prev) => (prev[idx] === empty ? prev : { ...prev, [idx]: empty }));

  const isHybrid = ahu.is_hybrid;
  // Only ever set on a hybrid AHU, which is the only place the X-Offset row
  // exists — a standard AHU's dual pair is aligned by the canvas and by
  // `tunnel_geometry._find_dual_primary`, and has no X-Offset field to lock.
  const xOffsetOwner = isHybrid
    ? xOffsetOwnerTunnel(tunnel, tunnels, sections)
    : null;
  const xOffsetTooltip = xOffsetOwner
    ? {
        className: ' ka-has-tooltip f-size-11',
        // Short on purpose: the bubble is drawn over the row it explains, so a
        // sentence would cover the value the user came to read.
        attr: {
          tooltip: `Set by tunnel ${formatTunnelAddress(xOffsetOwner, true)} (dual-tunnel component)`,
        },
      }
    : { className: '', attr: {} };

  return (
    <>
      {/* Legacy `tab.ahu.cabinet.tmpl:1-12` on KA-x-user-skill: the row is
          `li.tunnel-custom-name-li > div.single-line`, with the input FIRST and
          the caption after it as `span.label`. Nothing styles `.tunnel-tag-li`
          or a bare `<label>` here, so the earlier markup missed every data-bar
          row rule — `.sublist > li > div > .label` (34.89% wide, floated left,
          13px, #757575) and the `li:focus-within > div > .label` blue-on-focus
          cue, which both need the `div` container and the trailing span. */}
      <li className="tunnel-custom-name-li">
        <div className="single-line">
          <input
            className="tunnel_field"
            type="text"
            name="custom_name"
            maxLength={64}
            placeholder=""
            autoComplete="off"
            value={tagValue}
            onChange={(e) => setTagValue(e.currentTarget.value)}
            onBlur={(e) => saveTag(e.currentTarget.value)}
          />
          <span className="label">Tag</span>
        </div>
      </li>
      <li className="tunnel-dimensions-li">
        <div className="multiply-total multiply multiply-tunel ">
          <span className="unit">in</span>{' '}
          <span className="selection cabinet-sizes-selection">
            {Number(tunnel.width ?? 0).toFixed(2)}
          </span>{' '}
          <select
            className="small-input tunnel_field required-field"
            name="width"
            value={widthValue}
            onChange={(e) => changeTunnel('width', Number(e.currentTarget.value))}
          >
            {widths.map((w) => (
              <option key={String(w)} value={String(w)}>
                {String(w)}{' '}
              </option>
            ))}
          </select>{' '}
          <span className="unit large"> W </span> <span className="multiply-sign">x</span>{' '}
          <span className="selection cabinet-sizes-selection">
            {Number(tunnel.height ?? 0).toFixed(2)}
          </span>{' '}
          <select
            className="small-input tunnel_field required-field"
            name="height"
            value={heightValue}
            onChange={(e) => changeTunnel('height', Number(e.currentTarget.value))}
          >
            {heights.map((h) => (
              <option key={String(h)} value={String(h)}>
                {String(h)}
              </option>
            ))}
          </select>{' '}
          <span className="unit large"> H </span> <span className="multiply-sign">x</span>{' '}
          <input
            className="small-input tunnel_field"
            type="text"
            data-type="number"
            name="length"
            data-min={tunnel.length_min ?? 12}
            data-max={tunnel.length_max ?? 600}
            data-step="0.01"
            value={formatLegacyNumber(tunnel.length, 0.01)}
            tabIndex={-1}
            readOnly
            onChange={() => undefined}
          />{' '}
          <span className="unit large"> L</span>
        </div>
      </li>
      <li data-select-index="0" style={hiddenIfEmpty(0)}>
        <div className="single-line-with-custom-select">
          <div id="outer-skin-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/casing/metal_sheets"
              constructionClassId={ccid}
              value={(casing?.outerliner_id as number | null | undefined) ?? null}
              onChange={(id) => changeCasing('outerliner_id', id)}
              selectName="outerliner_id"
              selectClass="tunnel_casing_field"
              hasNoneOption={false}
              onEmptyChange={markEmpty(0)}
            />
          </div>
          <span className="label">Outer Skin</span>
        </div>
      </li>
      <li data-select-index="1" style={hiddenIfEmpty(1)}>
        <div className="single-line-with-custom-select">
          <div id="skin-finish-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/casing/finishes"
              constructionClassId={ccid}
              value={(casing?.finish_id as number | null | undefined) ?? null}
              onChange={(id) => changeCasing('finish_id', id)}
              selectName="finish_id"
              selectClass="tunnel_casing_field"
              onEmptyChange={markEmpty(1)}
            />
          </div>
          <span className="label">
            Skin Finish
          </span>
        </div>
      </li>
      <li data-select-index="2" style={hiddenIfEmpty(2)}>
        <div className="single-line-with-custom-select">
          <div id="under-insulation-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/floor/under_insulations"
              constructionClassId={ccid}
              value={(floor?.under_insulation_id as number | null | undefined) ?? null}
              onChange={(id) => changeFloor('under_insulation_id', id)}
              selectName="under_insulation_id"
              selectClass="tunnel_floor_field"
              onEmptyChange={markEmpty(2)}
            />
          </div>
          <span className="label">Floor Under-Insulation</span>
        </div>
      </li>
      <li data-select-index="3" style={hiddenIfEmpty(3)}>
        <div className="single-line-with-custom-select">
          <div id="under-liner-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/floor/under_liner_metal_sheets"
              constructionClassId={ccid}
              value={(floor?.underliner_id as number | null | undefined) ?? null}
              onChange={(id) => changeFloor('underliner_id', id)}
              selectName="underliner_id"
              selectClass="tunnel_floor_field"
              onEmptyChange={markEmpty(3)}
            />
          </div>
          <span className="label">Floor Under-Liner</span>
        </div>
      </li>

      {/* "Walls, Roof & Floor", then "Bases" — the same multi-select widget
          twice, hybrid AHUs only and Power-user only. Both are
          `TunnelFaceFlagMultiSelect`, which carries the markup, the popup
          placement and the ellipsis tooltip this row used to hold inline.
          WHEN each one writes is the row's own business: Walls/Roof & Floor
          batches to the close, Bases writes on a debounce after every toggle
          (`baseSidesWriter`). They also differ in which columns they write
          and what has to be re-read afterwards.

          Bases sits DIRECTLY BELOW Walls/Roof & Floor and above X-Offset, and
          it is a Data Bar row like any other: nothing about the Standard AHU
          data bar changes, because neither row renders off a hybrid unit. */}
      {isHybrid && isPowerUser && (
        <TunnelFaceFlagMultiSelect
          options={FACE_FLAG_FIELDS}
          values={faceFlagValues}
          onToggle={(name) => toggleFaceFlag(name as FaceFlagField)}
          onCommit={saveFaceFlags}
          caption={<>Walls, Roof &amp; Floor</>}
          buttonId="show_tunnel_face_flags"
        />
      )}

      {/* Hybrid only, by decision: a Standard AHU's bases are not grouped
          across tunnels and have no automatic end-vanishing rule to override,
          so there is nothing for this row to show there. A Standard unit
          renders neither this row nor the one above it, exactly as before.
          Nor does a tunnel with no Base chosen — there are no sides to tick. */}
      {isHybrid && isPowerUser && tunnel.base_id != null && (
        <TunnelFaceFlagMultiSelect
          options={BASE_SELECT_OPTIONS}
          values={baseSideValues}
          lockedReason={basesLockedReason}
          onToggle={toggleBaseSide}
          onCommit={saveBaseSides}
          caption="Bases"
          buttonId="show_tunnel_base_sides"
        />
      )}


      {/* X-Offset and Y-Offset follow Walls/Roof/Floor, and both rows are
          `li.tunnel-offset-li` — the order and the class names are
          tab.ahu.cabinet.tmpl:144-186 on KA-x-user-skill verbatim.

          The markup shape is load-bearing twice over. Neither row has a rule
          of its own anywhere in that branch's stylesheets (grepped: the only
          `.tunnel-offset-li` occurrences are the two `<li>`s); they are drawn
          entirely by the generic data-bar row rules, all of which key off
          `.sublist > li > div > …`. The earlier `li > label > input` shape
          matched none of them, so both rows fell back to browser defaults —
          no floated 34.89%-wide caption, no `in` unit, and on the Y row a
          native-looking <select> box, because the rule that makes every other
          dropdown in this tab render its text transparent behind a companion
          `.selection` span is `.inputs-group .sublist > li div select` and
          needs the `div`.

          Verified those generic rules are byte-identical between the served
          legacy sheet and KA-x-user-skill's, so matching the markup is
          sufficient — there is no CSS to port for these two rows. */}
      {isHybrid && (
        <li className="tunnel-offset-li">
          <div>
            <span className="unit">in</span>
            <input
              className="tunnel_field medium-input"
              type="text"
              data-type="number"
              data-formatter_type="decimal"
              data-step="0.01"
              name="x_offset"
              id={`x_offset_${tunnel.id}`}
              /* `x_offset` is not only typed here — reversing a hybrid
                 tunnel's airflow direction re-bases it server-side
                 (`airflowFlipPatch`, because the stored number names the
                 UPSTREAM edge and flipping changes which edge that is). The
                 row is keyed on `tunnel.id` and never remounts, and
                 `defaultValue` is applied at mount only, so the field kept
                 showing the pre-flip number until the board was reloaded —
                 and blurring it then PATCHed that stale number back, moving
                 the tunnel by its own length.

                 Legacy has no equivalent gap: `tab.ahu.tunnel.cabinet.js:293-316`
                 re-renders the whole cabinet template on ANY model change, so
                 the save inside `toggle_airflow_direction` repaints this input
                 with the new value. Keying the input on the stored value is
                 the same thing scoped to the one field — it remounts exactly
                 when legacy would have repainted, and (also as in legacy) a
                 FAILED save writes nothing, leaves the key alone and keeps
                 whatever the user typed. */
              key={formatLegacyNumber(tunnel.x_offset ?? 0, 0.01)}
              defaultValue={formatLegacyNumber(tunnel.x_offset ?? 0, 0.01)}
              /* The SECOND tunnel of a dual-tunnel component does not own this
                 number — see `xOffsetOwnerTunnel`. Disabled rather than
                 unmounted, the way legacy locks the Y-Offset input outside the
                 two user_defined_* directions: the row keeps its height and
                 still SHOWS the value, which is the useful part — it is where
                 the user reads where the pair ended up. The server recomputes
                 it on every save that can move either half, and the `key`
                 above reloads the box when it does. */
              disabled={!!xOffsetOwner}
              onBlur={(e) => {
                const value = Number(e.currentTarget.value);
                if (Number.isFinite(value)) changeOffsetField('x_offset', value);
              }}
            />
            <span className={`label${xOffsetTooltip.className}`} {...xOffsetTooltip.attr}>
              X-Offset (+/-)
            </span>
          </div>
        </li>
      )}

      {showYOffset(tunnel, tunnels, isHybrid) && (
        <li className="tunnel-offset-li">
          <div className="single-line">
            {/* §annotateTunnelOffsetState — the <select>'s own text is
                transparent, so the resolved label is what the user reads. */}
            <span className="selection">
              {TUNNEL_OFFSET_DIRECTIONS.find(
                (o) => o.value === (tunnel.tunnel_offset_direction ?? 'centered')
              )?.label ?? tunnel.tunnel_offset_direction}
            </span>
            <select
              className="tunnel_field"
              name="tunnel_offset_direction"
              style={{ width: '136px' }}
              id={`tunnel_offset_direction_${tunnel.id}`}
              value={tunnel.tunnel_offset_direction ?? 'centered'}
              onChange={(e) =>
                changeOffsetField('tunnel_offset_direction', e.currentTarget.value)
              }
            >
              {TUNNEL_OFFSET_DIRECTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="unit">in</span>
            {/* Legacy always renders this input and only DISABLES it outside
                the two user_defined_* directions ({{#unless
                tunnel.tunnel_offset_user_defined}}disabled{{/unless}}).
                Unmounting it instead, as this did, left the row a different
                height and width from legacy's. The `key` reproduces the
                re-render legacy gets for free: Backbone re-templates the whole
                view on every save, so the box always reloads the stored value
                when the direction changes. */}
            <input
              key={tunnel.tunnel_offset_direction ?? 'centered'}
              className="tunnel_field small-input"
              type="text"
              data-type="number"
              data-formatter_type="decimal"
              data-step="0.01"
              data-min="0"
              name="tunnel_offset"
              id={`tunnel_offset_${tunnel.id}`}
              defaultValue={formatLegacyNumber(tunnel.tunnel_offset ?? 0, 0.01)}
              disabled={
                tunnel.tunnel_offset_direction !== 'user_defined_left' &&
                tunnel.tunnel_offset_direction !== 'user_defined_right'
              }
              onBlur={(e) => {
                const value = Number(e.currentTarget.value);
                if (Number.isFinite(value) && value >= 0) {
                  changeOffsetField('tunnel_offset', value);
                }
              }}
            />
            <span className="label">Y Offset</span>
          </div>
        </li>
      )}
    </>
  );
};

export default TunnelCabinetRow;
